/** @fileoverview Renders account navigation using Platform profile data. */

import { useState } from 'react';
import { BarChart3, CircleHelp, CircleAlert, LogOut, Mail, MessageSquare } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import AccountBadge, { initials } from './AccountBadge';
import ThemeSelector from './ThemeSelector';
import UsageDialog from './UsageDialog';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { getHelpURL, logout } from '../services/platformAuth';
import type { Tenant, User } from '../types/identity';

type Props = {
  user: User;
  tenant?: Tenant;
  closed?: boolean;
  logoutOnly?: boolean;
  name?: string;
  onLogout?: () => void | Promise<void>;
};

const displayName = (user: User) => [user.first_name, user.last_name].filter(Boolean).join(' ');

const mailto = (kind: 'support' | 'feedback' | 'issue', user: User, tenant?: Tenant) => {
  const identity = `User: ${displayName(user) || 'Unknown'} (${user.email || ''})\nTenant: ${
    tenant?.name || ''
  } (${tenant?.tenant_id || ''})`;
  const details =
    kind === 'support'
      ? {
          subject: 'Need assistance with INTEGRTR.Fabrik',
          body: `Hello,\n\nI need some assistance with INTEGRTR.Fabrik. Following are my user details:\n\n${identity}\n\n`,
        }
      : {
          subject:
            kind === 'feedback'
              ? 'INTEGRTR.Fabrik | Application Feedback'
              : 'INTEGRTR.Fabrik | Found an Issue!',
          body: `\n\n\n-----------------------------------------------\n${identity}\n-----------------------------------------------`,
        };
  return `mailto:support@integrtr.com?subject=${encodeURIComponent(
    details.subject
  )}&body=${encodeURIComponent(details.body)}`;
};

export function FabrikNavSupportActions({ user, tenant }: Props) {
  const supportUrl = mailto('support', user, tenant);

  return (
    <>
      <Tooltip>
        <TooltipTrigger
          render={
            <a
              href={supportUrl}
              className="grid h-8 w-8 place-items-center rounded-md text-current hover:bg-current/10"
              aria-label="Contact Us"
            >
              <Mail className="h-5 w-5" aria-hidden="true" />
            </a>
          }
        />
        <TooltipContent>Contact Us</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger
          render={
            <a
              href={getHelpURL()}
              className="grid h-8 w-8 place-items-center rounded-md text-current hover:bg-current/10"
              target="_blank"
              rel="noreferrer"
              aria-label="Help"
            >
              <CircleHelp className="h-5 w-5" aria-hidden="true" />
            </a>
          }
        />
        <TooltipContent>Help</TooltipContent>
      </Tooltip>
    </>
  );
}

/** Reuses the Dataflow account menu pattern with Fabrik-specific support links. */
export default function AccountMenu({
  user,
  tenant,
  closed = false,
  logoutOnly = false,
  name,
  onLogout,
}: Props) {
  const accountName = name || user.first_name || user.email || 'Account';
  const feedbackUrl = mailto('feedback', user, tenant);
  const issueUrl = mailto('issue', user, tenant);
  const [usageOpen, setUsageOpen] = useState(false);

  if (closed) {
    return <AccountBadge user={user} name={accountName} />;
  }

  if (logoutOnly) {
    return (
      <div className="ml-auto">
        <DropdownMenu>
          <DropdownMenuTrigger className="flex h-9 items-center gap-2 px-1.5 text-sm font-semibold text-current">
            <span className="max-w-32 truncate">{accountName}</span>
            <Avatar size="sm">
              <AvatarImage src={user.picture} alt="" />
              <AvatarFallback className="bg-app-avatar font-semibold text-app-action-foreground">
                {initials(user)}
              </AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-48 rounded-sm bg-app-surface-card p-0 text-app-text shadow-md"
          >
            <DropdownMenuItem
              className="h-9 gap-3 rounded-none px-4 text-sm"
              onClick={() => {
                if (onLogout) void onLogout();
                else void logout();
              }}
            >
              <LogOut className="text-app-text-muted" aria-hidden="true" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger className="flex h-9 items-center gap-2 rounded-md px-1.5 text-sm font-semibold text-current hover:bg-current/10">
          <span className="max-w-32 truncate">{accountName}</span>
          <Avatar size="sm">
            <AvatarImage src={user.picture} alt="" />
            <AvatarFallback className="bg-app-avatar font-semibold text-app-action-foreground">
              {initials(user)}
            </AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-48 rounded-sm bg-app-surface-card p-0 text-app-text shadow-md"
        >
          <DropdownMenuItem
            className="h-9 gap-3 rounded-none px-4 text-sm"
            disabled={!tenant}
            onClick={() => setUsageOpen(true)}
          >
            <BarChart3 className="text-app-text-muted" aria-hidden="true" />
            View Usage
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-9 gap-3 rounded-none px-4 text-sm"
            onClick={() => window.location.assign(feedbackUrl)}
          >
            <MessageSquare className="text-app-text-muted" aria-hidden="true" />
            Share Feedback
          </DropdownMenuItem>
          <DropdownMenuItem
            variant="destructive"
            className="h-9 gap-3 rounded-none px-4 text-sm"
            onClick={() => window.location.assign(issueUrl)}
          >
            <CircleAlert aria-hidden="true" />
            Report Issue
          </DropdownMenuItem>
          <DropdownMenuSeparator className="mx-0" />
          <ThemeSelector />
          <DropdownMenuSeparator className="mx-0" />
          <DropdownMenuItem
            className="h-9 gap-3 rounded-none px-4 text-sm"
            onClick={() => void logout()}
          >
            <LogOut className="text-app-text-muted" aria-hidden="true" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <UsageDialog tenant={tenant} open={usageOpen} onOpenChange={setUsageOpen} />
    </div>
  );
}
